import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'

// Utility function to create speed options
const createSpeedOptions = (start: number, end: number, step: number): IOption[] => {
  return Array.from({ length: Math.floor((end - start) / step) + 1 }, (_, i) => {
    const speed = (start + i * step).toFixed(1)
    return {
      text: speed,
      value: parseFloat(speed),
    }
  })
}

interface SpeedSelectorProps {
  cleaningSpeed?: number
  transportSpeed?: number
  onCleaningSpeedChange?: (speed: number) => void
  onTransportSpeedChange?: (speed: number) => void
  disabled?: boolean
  showTransportSpeed?: boolean
  showCleaningSpeed?: boolean
  className?: string
}

const SpeedSelector: React.FC<SpeedSelectorProps> = ({
  cleaningSpeed = 0.5,
  transportSpeed = 0.7,
  onCleaningSpeedChange,
  onTransportSpeedChange,
  disabled = false,
  showTransportSpeed = true,
  showCleaningSpeed = true,
  className,
}) => {
  const { t } = useTranslation()

  const transportSpeedOptions: IOption[] = createSpeedOptions(0.2, 2.0, 0.1)
  const cleaningSpeedOptions: IOption[] = createSpeedOptions(0.2, 1.5, 0.1)

  const [transportSpeedOption, setTransportSpeedOption] = useState<IOption>(() => {
    const initialOption = transportSpeed ? { text: transportSpeed.toString(), value: transportSpeed } : transportSpeedOptions[0]

    return initialOption
  })
  
  const [cleaningSpeedOption, setCleaningSpeedOption] = useState<IOption>(() => {
    const initialOption = cleaningSpeed ? { text: cleaningSpeed.toString(), value: cleaningSpeed } : cleaningSpeedOptions[0]

    return initialOption
  })

  useEffect(() => {
    if (transportSpeed !== undefined) {
      const newOption = { text: transportSpeed.toString(), value: transportSpeed }
      setTransportSpeedOption(newOption)
    }
  }, [transportSpeed])

  useEffect(() => {
    if (cleaningSpeed !== undefined) {
      const newOption = { text: cleaningSpeed.toString(), value: cleaningSpeed }
      setCleaningSpeedOption(newOption)
    }
  }, [cleaningSpeed])

  const onTransportSpeedChanged = (option: IOption) => {
    setTransportSpeedOption(option)
    onTransportSpeedChange?.(option.value as number)
  }

  const onCleaningSpeedChanged = (option: IOption) => {
    setCleaningSpeedOption(option)
    onCleaningSpeedChange?.(option.value as number)
  }

  return (
    <div className={className}>
      {showTransportSpeed && (
        <Dropdown
          className="speed-dropdown"
          currentOption={transportSpeedOption}
          options={transportSpeedOptions}
          onClickOption={onTransportSpeedChanged}
          description={t('mission_settings.transport_speed')}
          disabled={disabled}
        />
      )}
      {showCleaningSpeed && (
        <Dropdown
          className="speed-dropdown"
          currentOption={cleaningSpeedOption}
          options={cleaningSpeedOptions}
          onClickOption={onCleaningSpeedChanged}
          description={t('mission_settings.cleaning_speed')}
          disabled={disabled}
        />
      )}
    </div>
  )
}

export default SpeedSelector 